import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useFormWithSchema from "../../hooks/useFormWithSchema";
import * as Yup from "yup";
import i18n from "../../i18n";
import { Controller } from "react-hook-form";
import { Button } from "../button";
import TextInput from "../inputs/TextInput";
import CheckboxInput from "../inputs/CheckboxInput";
import { useGetFaceRecognition } from "../../hooks/common/query";
import Loader from "../loader";
import {
  useSyncFRRecognition,
  useUpdateFaceRecognition,
} from "../../hooks/common/mutation";
import toast from "react-hot-toast";

export const FaceRecognitionFRConfigurationsCardSchema = Yup.object({
  recognitionThreshold: Yup.number().required(
    i18n.language === "en"
      ? "Recognition threshold is required."
      : "يجب أن يكون هناك حد أدنى للاعتراف."
  ),
  maxRecognitionDistance: Yup.number().required(
    i18n.language === "en"
      ? "Max recognition distance is required."
      : "يجب أن تكون مسافة التعرف القصوى."
  ),
  secondRecognitionInterval: Yup.number().required(
    i18n.language === "en"
      ? "Second recognition interval is required."
      : "يجب أن تكون هناك فترة ثانية للتعرف."
  ),
  duplicateEnrollmentThreshold: Yup.number().required(
    i18n.language === "en"
      ? "Duplicate Enrollment Threshold is required."
      : "يجب أن يكون هناك حد أدنى للتسجيل المكرر."
  ),
});

interface FormData {
  recognitionThreshold: number;
  maxRecognitionDistance: number;
  secondRecognitionInterval: number;
  duplicateEnrollmentThreshold: number;
}

interface FaceRecognitionFRConfigurationsCardProps {
  selectedDeviceIPAddresses: string[];
}

const FaceRecognitionFRConfigurationsCard = ({
  selectedDeviceIPAddresses,
}: FaceRecognitionFRConfigurationsCardProps) => {
  const { t } = useTranslation();

  const [isMaskEnable, setIsMaskEnable] = useState<boolean>(false);
  const [isDuplicateEnrollment, setIsDuplicateEnrollment] =
    useState<boolean>(false);

  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useFormWithSchema(FaceRecognitionFRConfigurationsCardSchema);

  const { data: faceRecognition, isPending: isFaceRecognitionLoading } =
    useGetFaceRecognition();

  const {
    mutateAsync: UpdateFaceRecognition,
    isPending: isUpdateFaceRecognitionLoading,
  } = useUpdateFaceRecognition();

  const {
    mutateAsync: SyncFRRecognition,
    isPending: isSyncFRRecognitionLoading,
  } = useSyncFRRecognition();

  useEffect(() => {
    if (faceRecognition) {
      reset({
        recognitionThreshold: faceRecognition.recognitionThreshold,
        maxRecognitionDistance: faceRecognition.maxRecognitionDistance,
        secondRecognitionInterval: faceRecognition.secondRecognitionInterval,
        duplicateEnrollmentThreshold:
          faceRecognition.duplicateEnrollmentThreshold,
      });
      setIsMaskEnable(faceRecognition.isMaskMode);
      setIsDuplicateEnrollment(faceRecognition.isDuplicateEnrollment);
    }
  }, [faceRecognition, reset]);

  const onSubmit = useCallback(
    async (data: FormData) => {
      try {
        const body = {
          isMaskMode: isMaskEnable,
          recognitionThreshold: data.recognitionThreshold,
          maxRecognitionDistance: data.maxRecognitionDistance,
          secondRecognitionInterval: data.secondRecognitionInterval,
          isDuplicateEnrollment: isDuplicateEnrollment,
          duplicateEnrollmentThreshold: data.duplicateEnrollmentThreshold,
        };
        await UpdateFaceRecognition(body);

        toast.success(t("faceRecognitionConfigurationUpdatedSuccessfully"));
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }

      if (selectedDeviceIPAddresses.length === 0) {
        toast.error(t("kindlySelectDeviceToSync"));
      } else {
        try {
          const body = {
            isMaskMode: isMaskEnable,
            recognitionThreshold: data.recognitionThreshold,
            maxRecognitionDistance: data.maxRecognitionDistance,
            secondRecognitionInterval: data.secondRecognitionInterval,
            isDuplicateEnrollment: isDuplicateEnrollment,
            duplicateEnrollmentThreshold: data.duplicateEnrollmentThreshold,
            fripAddresses: selectedDeviceIPAddresses,
          };

          await SyncFRRecognition(body);
          toast.success(t("faceRecognitionConfigurationSyncedSuccessfully"));
        } catch (error) {
          toast.error(t("somethingWentWrong"));
        }
      }
    },
    [
      SyncFRRecognition,
      UpdateFaceRecognition,
      isDuplicateEnrollment,
      isMaskEnable,
      selectedDeviceIPAddresses,
      t,
    ]
  );

  return (
    <div className="flex flex-col gap-2 lg:gap-3">
      <h2 className="text-lg lg:text-xl font-medium">
        {t("faceRecognitionConfiguration")}
      </h2>

      {isFaceRecognitionLoading ? (
        <Loader />
      ) : (
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-2 md:gap-4"
        >
          <div className="grid sm:grid-cols-3 gap-2 md:gap-4 items-end">
            <Controller
              name="recognitionThreshold"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  type="number"
                  label={t("recognitionThreshold")}
                  onChange={onChange}
                  value={value?.toString() || ""}
                  error={errors?.[name]?.message}
                />
              )}
            />

            <Controller
              name="maxRecognitionDistance"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  type="number"
                  label={t("maxRecognitionDistance")}
                  onChange={onChange}
                  value={value?.toString() || ""}
                  error={errors?.[name]?.message}
                />
              )}
            />

            <Controller
              name="secondRecognitionInterval"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  type="number"
                  label={t("secondRecognitionInterval")}
                  onChange={onChange}
                  value={value?.toString() || ""}
                  error={errors?.[name]?.message}
                />
              )}
            />

            <Controller
              name="duplicateEnrollmentThreshold"
              control={control}
              render={({ field: { onChange, name, value } }) => (
                <TextInput
                  type="number"
                  label={t("duplicateEnrollmentThreshold")}
                  onChange={onChange}
                  value={value?.toString() || ""}
                  error={errors?.[name]?.message}
                />
              )}
            />
          </div>

          <div className="flex items-center gap-6">
            <CheckboxInput
              key="mask-enable"
              id="mask-enable"
              label={t("maskEnable")}
              checked={isMaskEnable}
              setChecked={() => setIsMaskEnable((prev) => !prev)}
            />

            <CheckboxInput
              key="duplicate-enrollment"
              id="duplicate-enrollment"
              label={t("duplicateEnrollment")}
              checked={isDuplicateEnrollment}
              setChecked={() => setIsDuplicateEnrollment((prev) => !prev)}
            />
          </div>

          <Button
            isLoading={
              isUpdateFaceRecognitionLoading || isSyncFRRecognitionLoading
            }
            className="max-w-max !py-[14px] !px-9"
          >
            {t("pushSync")}
          </Button>
        </form>
      )}
    </div>
  );
};

export default React.memo(FaceRecognitionFRConfigurationsCard);
